import type { TFunction } from 'i18next';
import { useCallback, useEffect, useRef, useState } from 'react';

import { type WorkflowTemplate, workflowsApi } from '@/api/workflows';
import { handleApiError } from '@/lib/handleApiError';
import { message } from '@/lib/message';

import { forgetEditingWorkflow, rememberEditingWorkflow } from './editorLock';
import { extractApiError } from './statusUtils';

/**
 * State that drives the read-only banner ("Currently being edited by X — your changes
 * won't save"). Non-null means a foreign Originator holds the lock OR the TTL expired
 * to a non-owner — in either case the editor on this tab can't write, and we surface
 * the banner so the user knows up front.
 */
export type LockBlockState = {
  holderName?: string | null;
  expiresAt?: string | null;
};

export type UseEditorLockResult = {
  workflow: WorkflowTemplate | null;
  loading: boolean;
  /** When non-null, render read-only with the banner. */
  lockBlock: LockBlockState | null;
  /** Owner-only force-release: BE atomically clears the foreign lock + acquires it for the caller. */
  reclaim: () => Promise<void>;
  /** Patch the loaded workflow in-place after a successful editor mutation. */
  patchWorkflow: (fresh: WorkflowTemplate) => void;
};

/**
 * Consolidates the editor-lock lifecycle for the WorkflowDetailsPage:
 *   1. Mount: GET the template, conditionally acquire the lock (only for Draft/Rejected).
 *   2. Poll every 20s while we hold the lock — flip to read-only as soon as the owner
 *      reclaims (or our TTL expires).
 *   3. Reclaim: atomic force-release + re-acquire for the calling owner.
 *   4. Unmount: release the lock IF this mount acquired it (skipped for read-only viewers).
 *
 * One mutable ref (`acquiredLockRef`) tracks "does THIS tab hold the lock". All three
 * sites that change that state (mount-acquire, poll-loss, reclaim) live inside this
 * hook so the WorkflowDetailsPage caller stays declarative.
 *
 * @param id — workflow id from the route params; the hook no-ops while undefined.
 * @param oid — current caller's Entra oid; the poll needs it to recognize ownership.
 * @param t — i18next translation fn for error toasts.
 * @param onLoadError — called when the initial GET fails (e.g., to navigate away).
 */
export function useEditorLock(
  id: string | undefined,
  oid: string | null,
  t: TFunction,
  onLoadError: () => void,
): UseEditorLockResult {
  const [workflow, setWorkflow] = useState<WorkflowTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [lockBlock, setLockBlock] = useState<LockBlockState | null>(null);
  const acquiredLockRef = useRef(false);

  // Mount-time acquire + unmount release.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    (async () => {
      try {
        const loaded = await workflowsApi.get(id);
        if (cancelled) return;
        setWorkflow(loaded);

        const editable = loaded.status === 'Draft' || loaded.status === 'Rejected';
        if (!editable) {
          // Read-only by status (Pending under review, Approved live). No lock needed —
          // the lock chip / banner stay hidden, mutation controls are status-gated.
          return;
        }

        try {
          const acquired = await workflowsApi.acquireLock(id);
          if (cancelled) return;
          setWorkflow(acquired);
          rememberEditingWorkflow(id);
          acquiredLockRef.current = true;
        } catch (err) {
          if (cancelled) return;
          const apiErr = extractApiError(err);
          if (apiErr.status === 409 && apiErr.body && typeof apiErr.body === 'object') {
            // Lock held by someone else — leave `workflow` as the GET we already did and
            // surface the banner. Mutation buttons disable via lockedByOther downstream.
            const lockState = (
              apiErr.body as { lockState?: { holderName?: string; expiresAt?: string } }
            ).lockState;
            setLockBlock({
              holderName: lockState?.holderName ?? null,
              expiresAt: lockState?.expiresAt ?? null,
            });
            return;
          }
          // Any other failure path on acquire — log but don't bounce the user. They
          // can still see the read-only content from the GET above.
          message.error(apiErr.message ?? t('workflow.details.loadFailed'));
        }
      } catch (err) {
        if (cancelled) return;
        message.error(extractApiError(err).message ?? t('workflow.details.loadFailed'));
        onLoadError();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      // Active release (AC08) — only when this mount actually acquired the lock.
      // Skipping release on viewer-only mounts avoids both an unnecessary network call
      // and a multi-tab race where a sibling tab's editing id gets cleared by mistake.
      if (acquiredLockRef.current) {
        void workflowsApi.releaseLock(id).finally(() => forgetEditingWorkflow());
      }
    };
  }, [id, t, onLoadError]);

  // Periodic 20s lock-state poll while we hold the lock.
  //
  // The owner can reclaim a workflow at any time (POST /lock/force atomically clears
  // + re-acquires for them). A colleague who already has the editor open wouldn't find
  // out until they try to save — so we poll for it. Only runs while we actually hold
  // the lock; once we lose it (lockBlock set) the banner is up and there's no editing
  // session left to interrupt.
  //
  // KNOWN TRADEOFF: a 20s interval means up to 20 seconds of stale state. Bob can
  // keep typing for almost a full poll cycle after Alice reclaims, only to discover
  // on his next save that the BE has rejected him. The BE is the source of truth —
  // his saves 409 — so this never causes data corruption; it's purely a UX delay.
  // The proper fix (sub-second feedback) is push: a WebSocket / SSE / lambda-via-
  // API-Gateway channel that broadcasts lock-displacement events. That's a separate
  // sprint; until then 20s is the right interval — short enough that "huh, that
  // failed" is rare, long enough that a roomful of editors doesn't DoS the BE.
  useEffect(() => {
    if (!id || !workflow || !oid) return;
    if (lockBlock !== null) return;
    if (!workflow.lock || !workflow.lock.heldByCaller) return;
    const editable = workflow.status === 'Draft' || workflow.status === 'Rejected';
    if (!editable) return;

    let cancelled = false;
    const POLL_MS = 20_000;
    const tick = async () => {
      try {
        const fresh = await workflowsApi.get(id);
        if (cancelled) return;
        // BE computes heldByCaller for the requesting user; missing lock OR
        // someone else holds it means we lost our hold.
        if (!fresh.lock || !fresh.lock.heldByCaller) {
          // We lost the lock — owner reclaimed, or TTL expired. Either way, switch
          // to read-only so the user can't make changes that won't save.
          setWorkflow(fresh);
          setLockBlock({
            holderName: fresh.lock?.holderName ?? null,
            expiresAt: fresh.lock?.expiresAt ?? null,
          });
          // Don't fire a stray release on unmount — we no longer hold it.
          acquiredLockRef.current = false;
          forgetEditingWorkflow();
        } else {
          // Keep `workflow` fresh so the banner / chips stay accurate.
          setWorkflow(fresh);
        }
      } catch {
        // Best-effort — transient network errors don't bounce the user.
      }
    };
    const interval = window.setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [id, workflow, oid, lockBlock]);

  // Owner-only reclaim: BE atomically clears the foreign lock + acquires it for the
  // calling owner, so we go straight back into an editor session — no second
  // acquire-call from the FE. Updates state on success and marks the ref so unmount
  // fires DELETE /lock to release the newly-acquired lock.
  const reclaim = useCallback(async () => {
    if (!id) return;
    try {
      await workflowsApi.forceReleaseLock(id);
      const fresh = await workflowsApi.get(id);
      setWorkflow(fresh);
      setLockBlock(null);
      acquiredLockRef.current = true;
      rememberEditingWorkflow(id);
    } catch (err) {
      handleApiError(err, t, t('workflow.lock.forceReleaseFailed'));
    }
  }, [id, t]);

  return { workflow, loading, lockBlock, reclaim, patchWorkflow: setWorkflow };
}
